import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Camera, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { captureMutationError } from '../utils/errorReporting';

interface AvatarUploadProps {
  userId: string;
  currentUrl: string | null;
  label?: string;
  storageKey?: string; // e.g. "avatar" or "avatar-hover"
  onUploaded: (publicUrl: string) => void;
}

const AvatarUpload = ({
  userId,
  currentUrl,
  label = 'Profile photo',
  storageKey = 'avatar',
  onUploaded,
}: AvatarUploadProps) => {
  const [preview, setPreview] = useState<string | null>(currentUrl);
  const [isDragging, setIsDragging] = useState(false);

  // Sync preview when the parent updates currentUrl (e.g. form seeded from claimed profile)
  useEffect(() => {
    setPreview(currentUrl || null);
  }, [currentUrl]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useCallback(async (file: File) => {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5 MB.');
      return;
    }

    // Show local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setIsUploading(true);

    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
      // Deterministic path so re-uploads replace the existing file
      const path = `${userId}/${storageKey}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      // Bust cache with a timestamp so the browser picks up the new image
      const publicUrl = `${data.publicUrl}?t=${Date.now()}`;

      setPreview(publicUrl);
      onUploaded(publicUrl);
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'avatarUpload', table: 'storage.avatars', fileType: file.type, fileSize: file.size });
      setError(err instanceof Error ? err.message : 'Upload failed.');
      setPreview(currentUrl); // revert preview on error
    } finally {
      setIsUploading(false);
    }
  }, [userId, storageKey, currentUrl, onUploaded]);

  const handleFiles = (files: FileList | null) => {
    if (files?.[0]) upload(files[0]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleClear = () => {
    setPreview(null);
    onUploaded('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <label className="block text-xs text-bolt-textMuted mb-2">{label}</label>

      <div className="flex items-start gap-4">
        {/* Avatar preview */}
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-bolt-bg border-2 border-bolt-border flex items-center justify-center">
            {preview ? (
              <img src={preview} alt="Avatar preview" className="w-full h-full object-cover object-center" />
            ) : (
              <Camera className="h-7 w-7 text-bolt-textSubtle" />
            )}
            {isUploading && (
              <div className="absolute inset-0 bg-bolt-bg/70 flex items-center justify-center rounded-xl">
                <Loader className="h-5 w-5 text-primary-400 animate-spin" />
              </div>
            )}
          </div>
          {preview && !isUploading && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-bolt-surface border border-bolt-border rounded-full flex items-center justify-center text-bolt-textSubtle hover:text-error-400 transition-colors"
              aria-label="Remove photo"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Drop zone */}
        <div
          className={`flex-1 border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors min-h-20 ${
            isDragging
              ? 'border-primary-500 bg-primary-600/10'
              : 'border-bolt-border hover:border-bolt-borderLight hover:bg-bolt-surfaceHover'
          }`}
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <Upload className="h-4 w-4 text-bolt-textSubtle" />
          <p className="text-xs text-bolt-textMuted text-center">
            <span className="text-primary-400 font-medium">Click to upload</span>
            {' '}or drag and drop
          </p>
          <p className="text-xs text-bolt-textSubtle">JPEG, PNG, WebP — max 5 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
        </div>
      </div>

      {error && (
        <p className="mt-2 text-xs text-error-400">{error}</p>
      )}
    </div>
  );
};

export default AvatarUpload;
